import { useState, useRef } from 'react';
import { useProject } from '../../contexts/ProjectContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { cn } from '@/lib/utils';
import {
  Upload,
  Loader2,
  AlertCircle,
  Check,
  ArrowLeft,
  FileJson,
  FileSpreadsheet,
  FileCode,
  Info,
} from 'lucide-react';
import {
  parseImportFile,
  getAcceptedFileTypes,
  type ImportSource,
  type ImportResult,
} from '../../services/importParsers';
import { useCopy, fill } from '../../i18n';
import { plural } from '../../i18n/format';

interface ImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * `id`, `name` e `format` são identificadores (do parser e do produto de
 * origem), não interface: ficam aqui. Só a linha de descrição vem do
 * dicionário, indexada pelo `id`.
 */
interface SourceOption {
  id: ImportSource;
  name: string;
  icon: React.ReactNode;
  format: string;
}

const SOURCES: SourceOption[] = [
  {
    id: 'trello',
    name: 'Trello',
    icon: <FileJson size={24} className="text-blue-500" />,
    format: 'JSON',
  },
  {
    id: 'clickup',
    name: 'ClickUp',
    icon: <FileSpreadsheet size={24} className="text-purple-500" />,
    format: 'CSV',
  },
  {
    id: 'azure_devops',
    name: 'Azure DevOps',
    icon: <FileSpreadsheet size={24} className="text-sky-500" />,
    format: 'CSV',
  },
  {
    id: 'ms_project',
    name: 'MS Project',
    icon: <FileCode size={24} className="text-green-600" />,
    format: 'XML',
  },
];

type Step = 'select' | 'upload' | 'preview' | 'done';

const ImportDialog = ({ open, onOpenChange }: ImportDialogProps) => {
  const t = useCopy().modals.import;
  const { importProject } = useProject();
  const { hasFeature, isSystemAdmin } = useSubscription();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('select');
  const [selectedSource, setSelectedSource] = useState<ImportSource | null>(null);
  const [parsedResult, setParsedResult] = useState<ImportResult | null>(null);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [postWarnings, setPostWarnings] = useState<string[]>([]);

  const canImport = isSystemAdmin || hasFeature('import_external');

  const reset = () => {
    setStep('select');
    setSelectedSource(null);
    setParsedResult(null);
    setImporting(false);
    setError(null);
    setSuccessMessage(null);
    setProgress(null);
    setPostWarnings([]);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) reset();
    onOpenChange(open);
  };

  const handleSelectSource = (source: ImportSource) => {
    setSelectedSource(source);
    setError(null);
    setStep('upload');
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedSource) return;

    const MAX_SIZE = 10 * 1024 * 1024; // 10MB
    if (file.size > MAX_SIZE) {
      setError(t.errors.fileTooLarge);
      event.target.value = '';
      return;
    }

    setError(null);

    try {
      const content = await file.text();
      const result = parseImportFile(content, selectedSource, t.parser);

      // Count tasks
      const countTasks = (tasks: Array<Record<string, unknown>>): number => {
        let count = 0;
        for (const node of tasks) {
          count++;
          if (node.children && Array.isArray(node.children)) {
            count += countTasks(node.children as Array<Record<string, unknown>>);
          }
        }
        return count;
      };

      const totalTasks = countTasks(result.tasks as Array<Record<string, unknown>>);
      if (totalTasks > 5000) {
        setError(fill(t.errors.tooManyTasks, { count: totalTasks }));
        event.target.value = '';
        return;
      }

      setParsedResult(result);
      setStep('preview');
    } catch (err) {
      const msg = err instanceof Error ? err.message : t.errors.parse;
      setError(msg);
    }

    event.target.value = '';
  };

  const handleImport = async () => {
    if (!parsedResult) return;

    setImporting(true);
    setError(null);
    setProgress(null);
    setPostWarnings([]);

    try {
      const imported = await importProject(
        {
          project: parsedResult.project,
          tasks: parsedResult.tasks,
          // O quadro do Trello vem junto: sem isto, a pessoa migra as listas
          // dela e chega com o quadro padrão de cinco colunas.
          columns: parsedResult.columns,
        },
        {
          onProgress: (done, total) => setProgress({ done, total }),
          onWarning: (msg) => setPostWarnings((prev) => [...prev, msg]),
        }
      );

      if (imported) {
        setSuccessMessage(fill(t.success, { name: imported.name }));
        setStep('done');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : t.errors.generic;
      if (msg === 'PLAN_LIMIT_REACHED') {
        setError(t.errors.planLimit);
      } else {
        setError(fill(t.errors.withMessage, { message: msg }));
      }
    } finally {
      setImporting(false);
    }
  };

  const countTotalTasks = (): number => {
    if (!parsedResult) return 0;
    const count = (tasks: Array<Record<string, unknown>>): number => {
      let c = 0;
      for (const node of tasks) {
        c++;
        if (node.children && Array.isArray(node.children)) {
          c += count(node.children as Array<Record<string, unknown>>);
        }
      }
      return c;
    };
    return count(parsedResult.tasks as Array<Record<string, unknown>>);
  };

  const countGroups = (): number => {
    if (!parsedResult) return 0;
    const count = (tasks: Array<Record<string, unknown>>): number => {
      let c = 0;
      for (const node of tasks) {
        if (node.type === 'group') c++;
        if (node.children && Array.isArray(node.children)) {
          c += count(node.children as Array<Record<string, unknown>>);
        }
      }
      return c;
    };
    return count(parsedResult.tasks as Array<Record<string, unknown>>);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-[var(--color-text)]">
            {step === 'select' && t.title.select}
            {step === 'upload' && fill(t.title.upload, { source: SOURCES.find(s => s.id === selectedSource)?.name || '' })}
            {step === 'preview' && t.title.preview}
            {step === 'done' && t.title.done}
          </DialogTitle>
          <DialogDescription className="text-[var(--color-text-muted)]">
            {step === 'select' && t.description.select}
            {step === 'upload' && t.description.upload}
            {step === 'preview' && t.description.preview}
            {step === 'done' && ''}
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: Select source */}
        {step === 'select' && (
          <div className="grid grid-cols-2 gap-3">
            {SOURCES.map((source) => (
              <button
                key={source.id}
                onClick={() => canImport && handleSelectSource(source.id)}
                disabled={!canImport}
                className={cn(
                  'flex flex-col items-start gap-2 p-4 rounded-lg border border-[var(--color-border)] text-left transition-all',
                  canImport
                    ? 'hover:border-[var(--color-primary)] hover:bg-[var(--color-bg-secondary)] cursor-pointer'
                    : 'opacity-50 cursor-not-allowed'
                )}
              >
                <div className="flex items-center gap-3 w-full">
                  {source.icon}
                  <div>
                    <div className="text-sm font-medium text-[var(--color-text)]">{source.name}</div>
                    <div className="text-[11px] text-[var(--color-text-muted)]">{t.sources[source.id]}</div>
                  </div>
                </div>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)]">
                  {source.format}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Step 2: Upload file */}
        {step === 'upload' && selectedSource && (
          <div className="space-y-4">
            <button
              onClick={() => setStep('select')}
              className="flex items-center gap-1 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text)] transition-colors"
            >
              <ArrowLeft size={14} />
              {t.back}
            </button>

            {/* Instructions */}
            <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 text-[13px]">
              <Info size={16} className="mt-0.5 shrink-0" />
              <span>{t.instructions[selectedSource]}</span>
            </div>

            {/* Upload area */}
            <div
              onClick={() => fileInputRef.current?.click()}
              className="flex flex-col items-center justify-center gap-3 p-8 border-2 border-dashed border-[var(--color-border)] rounded-lg cursor-pointer hover:border-[var(--color-primary)] hover:bg-[var(--color-bg-secondary)] transition-all"
            >
              <Upload size={32} className="text-[var(--color-text-muted)]" />
              <div className="text-center">
                <p className="text-sm font-medium text-[var(--color-text)]">
                  {t.dropzone}
                </p>
                <p className="text-xs text-[var(--color-text-muted)] mt-1">
                  {fill(t.formatHint, { format: getAcceptedFileTypes(selectedSource).toUpperCase().replace('.', '') })}
                </p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept={getAcceptedFileTypes(selectedSource)}
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          </div>
        )}

        {/* Step 3: Preview */}
        {step === 'preview' && parsedResult && (
          <div className="space-y-4">
            <button
              onClick={() => { setStep('upload'); setParsedResult(null); }}
              className="flex items-center gap-1 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text)] transition-colors"
            >
              <ArrowLeft size={14} />
              {t.back}
            </button>

            {/* Summary */}
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 rounded-lg bg-[var(--color-bg-secondary)] text-center">
                <div className="text-2xl font-bold text-[var(--color-primary)]">{countTotalTasks()}</div>
                <div className="text-xs text-[var(--color-text-muted)]">{t.stats.tasks}</div>
              </div>
              <div className="p-3 rounded-lg bg-[var(--color-bg-secondary)] text-center">
                <div className="text-2xl font-bold text-[var(--color-primary)]">{countGroups()}</div>
                <div className="text-xs text-[var(--color-text-muted)]">{t.stats.groups}</div>
              </div>
              <div className="p-3 rounded-lg bg-[var(--color-bg-secondary)] text-center">
                <div className="text-2xl font-bold text-[var(--color-primary)]">
                  {parsedResult.tasks.filter(task => task.type !== 'group').length}
                </div>
                <div className="text-xs text-[var(--color-text-muted)]">{t.stats.roots}</div>
              </div>
            </div>

            {/* Project name */}
            <div className="p-3 rounded-lg border border-[var(--color-border)]">
              <div className="text-xs text-[var(--color-text-muted)] mb-1">{t.projectNameLabel}</div>
              <div className="text-sm font-medium text-[var(--color-text)]">{parsedResult.project.name}</div>
            </div>

            {/* Task preview (first 10) */}
            <div className="p-3 rounded-lg border border-[var(--color-border)] max-h-48 overflow-y-auto">
              <div className="text-xs text-[var(--color-text-muted)] mb-2">{t.previewLabel}</div>
              {parsedResult.tasks.slice(0, 10).map((task, i) => (
                <div key={i} className="flex items-center gap-2 py-1 text-[13px]">
                  <span className={cn(
                    'inline-block w-1.5 h-1.5 rounded-full shrink-0',
                    task.type === 'group' ? 'bg-blue-500' : task.type === 'milestone' ? 'bg-amber-500' : 'bg-green-500'
                  )} />
                  <span className="text-[var(--color-text)] truncate">{task.name}</span>
                  {task.assigned && (
                    <span className="text-[11px] text-[var(--color-text-muted)] shrink-0">
                      — {task.assigned as string}
                    </span>
                  )}
                </div>
              ))}
              {parsedResult.tasks.length > 10 && (
                <div className="text-xs text-[var(--color-text-muted)] mt-1">
                  {fill(plural(countTotalTasks() - 10, t.moreTasks), { count: countTotalTasks() - 10 })}
                </div>
              )}
            </div>

            {/* Warnings */}
            {parsedResult.warnings.length > 0 && (
              <div className="space-y-2">
                {parsedResult.warnings.map((w, i) => (
                  <div key={i} className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 dark:bg-amber-950/20 text-amber-700 dark:text-amber-300 text-[11px]">
                    <AlertCircle size={14} className="mt-0.5 shrink-0" />
                    <span>{w}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Import button */}
            <button
              onClick={handleImport}
              disabled={importing}
              className={cn(
                'w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all',
                importing
                  ? 'bg-[var(--color-primary)]/70 text-white cursor-wait'
                  : 'bg-[var(--color-primary)] text-white hover:opacity-90 cursor-pointer'
              )}
            >
              {importing ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  {progress ? fill(t.importingProgress, { done: progress.done, total: progress.total }) : t.importing}
                </>
              ) : (
                <>
                  <Upload size={16} />
                  {t.importButton}
                </>
              )}
            </button>

            {/* Barra de progresso (import grande: 1 lote por nível de hierarquia) */}
            {importing && progress && progress.total > 0 && (
              <div
                data-testid="import-progress"
                className="w-full h-1.5 bg-[var(--color-bg-secondary)] rounded-full overflow-hidden"
              >
                <div
                  className="h-full bg-[var(--color-primary)] transition-all duration-200"
                  style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }}
                />
              </div>
            )}
          </div>
        )}

        {/* Step 4: Done */}
        {step === 'done' && (
          <div className="flex flex-col items-center gap-4 py-4">
            <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <Check size={24} className="text-green-600 dark:text-green-400" />
            </div>
            <p className="text-sm font-medium text-[var(--color-text)] text-center">
              {successMessage}
            </p>
            {postWarnings.length > 0 && (
              <div className="w-full space-y-2">
                {postWarnings.map((w, i) => (
                  <div key={i} className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 dark:bg-amber-950/20 text-amber-700 dark:text-amber-300 text-[11px]">
                    <AlertCircle size={14} className="mt-0.5 shrink-0" />
                    <span>{w}</span>
                  </div>
                ))}
              </div>
            )}
            <button
              onClick={() => handleOpenChange(false)}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-[var(--color-primary)] text-white hover:opacity-90 transition-all"
            >
              {t.close}
            </button>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-950/20 text-red-700 dark:text-red-300 text-[13px]">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ImportDialog;
